import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, BarChart3, ShoppingCart, FileSpreadsheet, ArrowLeft, RefreshCw, PauseCircle, UserCircle2, ChevronDown, ChevronRight, Search, Mail, MessageCircle, Gift, FileText, Inbox, Receipt } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import BookingDetailModal from "../components/BookingDetailModal";
import AdminMfaGate from "../components/AdminMfaGate";
import QuoteBuilderTab from "../components/admin/QuoteBuilderTab";
import EnquiriesTab from "../components/admin/EnquiriesTab";
import InvoiceGeneratorTab from "../components/admin/InvoiceGeneratorTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabDefs = [
  { id: "traffic", label: "Traffic", icon: BarChart3 },
  { id: "sessions", label: "Sessions", icon: Users },
  { id: "customers", label: "Customers", icon: UserCircle2 },
  { id: "abandoned", label: "Abandoned (Step 5)", icon: ShoppingCart },
  { id: "unpaid", label: "Unpaid Bookings", icon: FileSpreadsheet },
  { id: "bookings", label: "All Bookings", icon: FileSpreadsheet },
  { id: "emi-pauses", label: "EMI Pauses", icon: PauseCircle },
  { id: "quote", label: "Quote Builder", icon: FileText },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "invoices", label: "Invoice Generator", icon: Receipt },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const fmtINR = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AdminPage({ user, isAuthenticated, authLoading }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("traffic");
  const [traffic, setTraffic] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [pauseStats, setPauseStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  // Map<user_id, {bookings: [...], loaded: bool}> — lazy-loaded bookings per
  // customer row so we don't fetch every customer's history on tab open.
  const [expandedCustomer, setExpandedCustomer] = useState({});
  // Track in-flight nudges per (userId, channel) so we can disable buttons
  // mid-request and avoid accidental double-sends.
  const [nudging, setNudging] = useState({});
  // Track in-flight promo generation per userId. Same UX pattern as `nudging`
  // — disables the button until the request resolves so admins can't double
  // click and accidentally mint two codes on a race (the backend dedupes
  // too, but we mirror the disabled state for clarity).
  const [generatingPromo, setGeneratingPromo] = useState({});
  // MFA gate state — we don't render the admin content until the gate
  // signals pass (either because MFA was already valid or it just succeeded).
  const [mfaPassed, setMfaPassed] = useState(false);

  // Guard: only admins. We don't trust frontend-only checks; the backend
  // re-verifies via require_admin on every endpoint. We wait until the
  // initial auth probe finishes so a freshly-loaded /admin route doesn't
  // bounce before the cookie-based user is resolved.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/booking");
      return;
    }
    if (user && user.is_admin === false) {
      toast.error("Admin access required");
      navigate("/booking");
    }
  }, [isAuthenticated, user, navigate, authLoading]);

  const loadTraffic = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/traffic/summary`);
      setTraffic(res.data);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load traffic: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/traffic/sessions`);
      setSessions(res.data.sessions || []);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load sessions: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAbandoned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/abandoned/step5`);
      setAbandoned(res.data.sessions || []);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load abandoned carts: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBookings = useCallback(async (status) => {
    setLoading(true);
    try {
      const url = status ? `${API}/admin/bookings?status=${status}` : `${API}/admin/bookings`;
      const res = await axios.get(url);
      setBookings(res.data.bookings || []);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load bookings: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPauseStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/emi/pause-stats`);
      setPauseStats(res.data);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load EMI pause stats: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCustomers = useCallback(async (q) => {
    setLoading(true);
    try {
      const url = q ? `${API}/admin/customers?q=${encodeURIComponent(q)}` : `${API}/admin/customers`;
      const res = await axios.get(url);
      setCustomers(res.data.customers || []);
    } catch (err) {
      if (err.response?.data?.detail !== "MFA_REQUIRED") {
        toast.error("Failed to load customers: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleCustomerRow = useCallback(async (userId) => {
    setExpandedCustomer((prev) => {
      const next = { ...prev };
      if (next[userId]?.open) {
        next[userId] = { ...next[userId], open: false };
      } else {
        next[userId] = { open: true, loaded: !!next[userId]?.loaded, bookings: next[userId]?.bookings || [] };
      }
      return next;
    });
    // Lazy-fetch bookings only the first time the row is expanded.
    setExpandedCustomer((prev) => prev);
    const existing = expandedCustomer[userId];
    if (!existing?.loaded) {
      try {
        const res = await axios.get(`${API}/admin/customers/${userId}/bookings`);
        setExpandedCustomer((prev) => ({
          ...prev,
          [userId]: { open: true, loaded: true, bookings: res.data.bookings || [] },
        }));
      } catch (err) {
        toast.error("Failed to load bookings: " + (err.response?.data?.detail || err.message));
      }
    }
  }, [expandedCustomer]);

  const nudgeCustomer = useCallback(async (userId, channel, e) => {
    if (e) e.stopPropagation();
    const key = `${userId}:${channel}`;
    setNudging((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/admin/customers/${userId}/nudge`, { channel });
      const label = channel === "email" ? "Email" : "WhatsApp";
      toast.success(`${label} nudge sent`);
      // Optimistically reflect the new last_nudge on the row without a full reload.
      setCustomers((prev) => prev.map((c) =>
        c.id === userId
          ? { ...c, last_nudge: { channel, at: res.data.at, status: "sent" } }
          : c
      ));
    } catch (err) {
      toast.error(`Nudge failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setNudging((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, []);

  // One-click promo code generator (Iter 47). Mints (or reuses) a single-use
  // promo for this customer and auto-copies a ready-to-share booking URL to
  // the admin's clipboard so they can hand-deliver the code on a phone call
  // or external chat without leaving the dashboard.
  const generatePromoForCustomer = useCallback(async (userId, e) => {
    if (e) e.stopPropagation();
    setGeneratingPromo((prev) => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.post(`${API}/admin/customers/${userId}/generate-promo`, {});
      const { code, discount_pct, expires_at, share_url, reused } = res.data || {};
      // Auto-copy the URL — admins almost always paste this somewhere right
      // after clicking. We fall back to copying just the code if the
      // Clipboard API isn't available (e.g. http-served dev tunnels).
      let copiedWhat = "";
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(share_url || code);
          copiedWhat = share_url ? "link" : "code";
        }
      } catch {
        // Clipboard write can throw on insecure contexts — surface in toast.
      }
      const exp = expires_at ? new Date(expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";
      const prefix = reused ? "Reused" : "Generated";
      const expClause = exp ? ` · expires ${exp}` : "";
      const tail = copiedWhat ? ` — ${copiedWhat} copied!` : "";
      toast.success(`${prefix} ${code} (${discount_pct}% off${expClause})${tail}`);
    } catch (err) {
      toast.error(`Could not generate promo: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGeneratingPromo((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || (user && user.is_admin === false)) return;
    // Don't fire admin data loads until MFA has been cleared — otherwise
    // each tab triggers a 401 MFA_REQUIRED and clutters the UI with toasts.
    if (!mfaPassed) return;
    if (tab === "traffic") loadTraffic();
    else if (tab === "sessions") loadSessions();
    else if (tab === "customers") loadCustomers();
    else if (tab === "abandoned") loadAbandoned();
    else if (tab === "unpaid") loadBookings("unpaid");
    else if (tab === "bookings") loadBookings();
    else if (tab === "emi-pauses") loadPauseStats();
  }, [tab, isAuthenticated, user, authLoading, mfaPassed, loadTraffic, loadSessions, loadCustomers, loadAbandoned, loadBookings, loadPauseStats]);

  const handleExport = (type) => {
    // CSV download — axios with responseType blob, then trigger save.
    axios.get(`${API}/admin/export?type=${type}`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error("Export failed: " + (err.response?.data?.detail || err.message)));
  };

  const openBookingDetail = async (b) => {
    setDetailBooking(b);
    try {
      const res = await axios.get(`${API}/bookings/${b.id}`);
      if (res.data) setDetailBooking(res.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Admin booking detail fetch failed; using list row:", e?.message || e);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="admin-page">
      {/* MFA gate: blocks every admin call until the JWT is mfa_passed.
          Once cleared, this returns null and the dashboard renders. */}
      {!mfaPassed && isAuthenticated && user?.is_admin !== false && (
        <AdminMfaGate onPass={() => setMfaPassed(true)} />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/booking")}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-serif text-2xl text-white">Admin Panel</h1>
              <p className="text-white/40 text-xs mt-0.5">{user?.name || user?.email || user?.phone}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (tab === "traffic") loadTraffic();
              else if (tab === "sessions") loadSessions();
              else if (tab === "customers") loadCustomers(customerSearch);
              else if (tab === "abandoned") loadAbandoned();
              else if (tab === "unpaid") loadBookings("unpaid");
              else if (tab === "bookings") loadBookings();
              else if (tab === "emi-pauses") loadPauseStats();
            }}
            disabled={loading}
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-6 overflow-x-auto">
          {tabDefs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active ? "border-[#D4AF37] text-white" : "border-transparent text-white/50 hover:text-white"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* TRAFFIC TAB */}
        {tab === "traffic" && (
          <div className="space-y-6" data-testid="admin-traffic">
            {!traffic ? (
              <p className="text-white/40 text-sm">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {["today", "week", "month"].map((k) => (
                    <div key={k} className="bg-white/[0.03] border border-white/5 rounded-lg p-4" data-testid={`traffic-card-${k}`}>
                      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2">{k}</p>
                      <p className="text-3xl text-white font-light">{traffic.counters[k].visits}</p>
                      <p className="text-[#D4AF37] text-xs mt-1">
                        {traffic.counters[k].unique} unique visitors
                      </p>
                    </div>
                  ))}
                </div>

                {/* Timeseries */}
                <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                  <h3 className="text-white/80 text-sm font-medium mb-3">Daily Visits (last 30 days)</h3>
                  {traffic.timeseries.length === 0 ? (
                    <p className="text-white/30 text-xs">No data yet</p>
                  ) : (
                    <div className="flex items-end gap-1 h-32 overflow-x-auto">
                      {traffic.timeseries.map((d) => {
                        const maxV = Math.max(...traffic.timeseries.map((x) => x.visits), 1);
                        const h = Math.max(4, (d.visits / maxV) * 100);
                        return (
                          <div key={d.date} className="flex flex-col items-center gap-1 min-w-[18px]" title={`${d.date}: ${d.visits} visits, ${d.unique} unique`}>
                            <div className="w-3 bg-[#D4AF37]/40 rounded-t" style={{ height: `${h}%` }} />
                            <span className="text-[8px] text-white/30 rotate-45 origin-left">{d.date.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Top pages */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <h3 className="text-white/80 text-sm font-medium mb-3">Top Pages</h3>
                    <div className="space-y-1.5 text-sm">
                      {traffic.top_pages.map((p) => (
                        <div key={p.page || "unknown"} className="flex justify-between text-white/60">
                          <span className="truncate mr-3">{p.page || "(unknown)"}</span>
                          <span className="text-white">{p.visits}</span>
                        </div>
                      ))}
                      {traffic.top_pages.length === 0 && (
                        <p className="text-white/30 text-xs">No data yet</p>
                      )}
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <h3 className="text-white/80 text-sm font-medium mb-3">Top Destinations Viewed</h3>
                    <div className="space-y-1.5 text-sm">
                      {traffic.top_destinations.map((d) => (
                        <div key={d.destination_id} className="flex justify-between text-white/60">
                          <span className="truncate mr-3">{d.destination_id}</span>
                          <span className="text-white">{d.views}</span>
                        </div>
                      ))}
                      {traffic.top_destinations.length === 0 && (
                        <p className="text-white/30 text-xs">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* SESSIONS TAB */}
        {tab === "sessions" && (
          <div data-testid="admin-sessions">
            <div className="flex justify-between items-center mb-3">
              <p className="text-white/40 text-xs">{sessions.length} sessions</p>
              <Button size="sm" variant="ghost" onClick={() => handleExport("sessions")} data-testid="export-sessions-btn">
                Export CSV
              </Button>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3">Last seen</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Events</th>
                    <th className="text-left p-3">Max Step</th>
                    <th className="text-left p-3">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-3 text-white/80">{fmtDateTime(s.last_seen)}</td>
                      <td className="p-3 text-white/80">
                        {s.user ? `${s.user.name || "—"} (${s.user.phone || s.user.email || ""})` : <span className="text-white/30">Anonymous</span>}
                      </td>
                      <td className="p-3 text-white/60">{s.events}</td>
                      <td className="p-3 text-white/60">{s.max_step || "—"}</td>
                      <td className="p-3 text-white/30">{s.ip || "—"}</td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-white/30">No sessions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CUSTOMERS TAB */}
        {tab === "customers" && (
          <div data-testid="admin-customers">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <p className="text-white/40 text-xs" data-testid="customers-count">{customers.length} customers</p>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-72">
                  <Search className="w-3.5 h-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="customer-search-input"
                    placeholder="Search name, email, phone, pincode…"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") loadCustomers(customerSearch); }}
                    className="bg-white/5 border-white/10 text-white text-xs pl-9 h-9"
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => loadCustomers(customerSearch)}
                  data-testid="customer-search-btn"
                >
                  Search
                </Button>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 w-6"></th>
                    <th className="text-left p-3">Joined</th>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Pincode</th>
                    <th className="text-left p-3">Bookings</th>
                    <th className="text-left p-3">Total Paid</th>
                    <th className="text-left p-3">Latest Activity</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => {
                    const exp = expandedCustomer[c.id];
                    const open = !!exp?.open;
                    const latest = c.last_booking || c.last_selection;
                    return (
                      <Fragment key={c.id}>
                        <tr
                          className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                          onClick={() => toggleCustomerRow(c.id)}
                          data-testid={`customer-row-${c.id.slice(0, 8)}`}
                        >
                          <td className="p-3 text-white/40">
                            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </td>
                          <td className="p-3 text-white/70">{fmtDateTime(c.created_at)}</td>
                          <td className="p-3 text-white">
                            {c.name || <span className="text-white/30">—</span>}
                            {c.is_admin && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#D4AF37]">admin</span>}
                          </td>
                          <td className="p-3 text-white/70">{c.email || <span className="text-white/30">—</span>}</td>
                          <td className="p-3 text-white/70">{c.phone || <span className="text-white/30">—</span>}</td>
                          <td className="p-3 text-white/70">{c.pincode || <span className="text-white/30">—</span>}</td>
                          <td className="p-3 text-white/80">
                            {c.total_bookings > 0 ? (
                              <span>
                                <span className="text-white">{c.total_bookings}</span>
                                {c.completed_bookings > 0 && (
                                  <span className="text-emerald-400/80 ml-1">({c.completed_bookings} done)</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-white/30">0</span>
                            )}
                          </td>
                          <td className="p-3 text-white">
                            {c.total_paid > 0 ? fmtINR(c.total_paid) : <span className="text-white/30">—</span>}
                          </td>
                          <td className="p-3 text-white/60">
                            {c.last_booking ? (
                              <span>
                                {c.last_booking.destination_name}
                                <span className="text-white/30 ml-1 capitalize">· {c.last_booking.package_type}</span>
                              </span>
                            ) : c.last_selection ? (
                              <span className="text-white/40">
                                Step {c.last_selection.step}
                                {c.last_selection.destination_id && ` · ${c.last_selection.destination_id}`}
                              </span>
                            ) : (
                              <span className="text-white/30">—</span>
                            )}
                          </td>
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                title={c.email ? `Send resume-booking email to ${c.email}` : "No email on file"}
                                disabled={!c.email || !!nudging[`${c.id}:email`]}
                                onClick={(e) => nudgeCustomer(c.id, "email", e)}
                                className={`p-1.5 rounded transition-colors ${c.email ? "text-white/60 hover:text-[#D4AF37] hover:bg-white/5" : "text-white/15 cursor-not-allowed"} ${nudging[`${c.id}:email`] ? "animate-pulse" : ""}`}
                                data-testid={`nudge-email-${c.id.slice(0, 8)}`}
                              >
                                <Mail className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                title={c.phone ? `Send resume-booking WhatsApp to ${c.phone}` : "No phone on file"}
                                disabled={!c.phone || !!nudging[`${c.id}:whatsapp`]}
                                onClick={(e) => nudgeCustomer(c.id, "whatsapp", e)}
                                className={`p-1.5 rounded transition-colors ${c.phone ? "text-white/60 hover:text-[#25D366] hover:bg-white/5" : "text-white/15 cursor-not-allowed"} ${nudging[`${c.id}:whatsapp`] ? "animate-pulse" : ""}`}
                                data-testid={`nudge-whatsapp-${c.id.slice(0, 8)}`}
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                title="Generate (or reuse) a one-time promo code & copy share link"
                                disabled={!!generatingPromo[c.id]}
                                onClick={(e) => generatePromoForCustomer(c.id, e)}
                                className={`p-1.5 rounded transition-colors text-white/60 hover:text-[#D4AF37] hover:bg-white/5 ${generatingPromo[c.id] ? "animate-pulse" : ""}`}
                                data-testid={`generate-promo-${c.id.slice(0, 8)}`}
                              >
                                <Gift className="w-3.5 h-3.5" />
                              </button>
                              {c.last_nudge && (
                                <span className="text-[10px] text-white/30 ml-1" title={`Last nudge: ${c.last_nudge.channel} on ${fmtDateTime(c.last_nudge.at)}`}>
                                  {fmtDateTime(c.last_nudge.at).split(",")[0]}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-white/[0.015]">
                            <td colSpan={10} className="p-4 border-b border-white/5" data-testid={`customer-detail-${c.id.slice(0, 8)}`}>
                              {!exp?.loaded ? (
                                <p className="text-white/40 text-xs">Loading bookings…</p>
                              ) : exp.bookings.length === 0 ? (
                                <p className="text-white/40 text-xs">No bookings yet. {c.last_selection ? `Last seen on Step ${c.last_selection.step}.` : ""}</p>
                              ) : (
                                <div className="space-y-2">
                                  <p className="text-white/40 text-[10px] uppercase tracking-wider">Booking History</p>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="text-white/30 text-[10px] uppercase tracking-wider">
                                        <tr>
                                          <th className="text-left py-2">Created</th>
                                          <th className="text-left py-2">Destination</th>
                                          <th className="text-left py-2">Package</th>
                                          <th className="text-left py-2">Dates</th>
                                          <th className="text-left py-2">Travelers</th>
                                          <th className="text-left py-2">Total</th>
                                          <th className="text-left py-2">Status</th>
                                          <th className="text-left py-2"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {exp.bookings.map((b) => (
                                          <tr key={b.id} className="border-t border-white/5">
                                            <td className="py-2 text-white/70">{fmtDateTime(b.created_at)}</td>
                                            <td className="py-2 text-white/80">{b.destination_name}</td>
                                            <td className="py-2 text-white/70 capitalize">{b.package_type}</td>
                                            <td className="py-2 text-white/60">{b.start_date} → {b.end_date}</td>
                                            <td className="py-2 text-white/60">{(b.adults || 0)}A {(b.children || 0)}C {(b.infants || 0)}I</td>
                                            <td className="py-2 text-white">{fmtINR(b.total_cost)}</td>
                                            <td className="py-2">
                                              <span className={`capitalize ${b.payment_status === "completed" ? "text-green-400" : "text-amber-400"}`}>
                                                {b.payment_status || "pending"}
                                              </span>
                                            </td>
                                            <td className="py-2">
                                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openBookingDetail(b); }} data-testid={`customer-view-booking-${b.id?.slice(0, 8)}`}>
                                                View
                                              </Button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {customers.length === 0 && (
                    <tr><td colSpan={10} className="p-6 text-center text-white/30">No customers match the search</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ABANDONED TAB */}
        {tab === "abandoned" && (
          <div data-testid="admin-abandoned">
            <div className="flex justify-between items-center mb-3">
              <p className="text-white/40 text-xs">{abandoned.length} sessions reached Step 5 but didn't book</p>
              <Button size="sm" variant="ghost" onClick={() => handleExport("abandoned")} data-testid="export-abandoned-btn">
                Export CSV
              </Button>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3">Last seen</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Destination</th>
                    <th className="text-left p-3">Package</th>
                    <th className="text-left p-3">Reached</th>
                  </tr>
                </thead>
                <tbody>
                  {abandoned.map((s) => (
                    <tr key={s.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-3 text-white/80">{fmtDateTime(s.last_seen)}</td>
                      <td className="p-3 text-white/80">
                        {s.user ? `${s.user.name || "—"} (${s.user.phone || s.user.email || ""})` : <span className="text-white/30">Anonymous</span>}
                      </td>
                      <td className="p-3 text-white/60">{s.destination_id || "—"}</td>
                      <td className="p-3 text-white/60 capitalize">{s.package_type || "—"}</td>
                      <td className="p-3 text-white/60">Step {s.max_step}</td>
                    </tr>
                  ))}
                  {abandoned.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-white/30">No abandoned carts yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* UNPAID / ALL BOOKINGS */}
        {(tab === "unpaid" || tab === "bookings") && (
          <div data-testid={`admin-${tab}`}>
            <div className="flex justify-between items-center mb-3">
              <p className="text-white/40 text-xs">{bookings.length} {tab === "unpaid" ? "unpaid" : "total"} bookings</p>
              <Button size="sm" variant="ghost" onClick={() => handleExport("bookings")} data-testid="export-bookings-btn">
                Export CSV
              </Button>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3">Created</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Destination</th>
                    <th className="text-left p-3">Package</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3"># EMIs</th>
                    <th className="text-left p-3">EMIs Paid</th>
                    <th className="text-left p-3">Collected</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    // Derive the three new columns directly from booking shape.
                    // Full payment plan has no schedule → show "—" for EMI counts.
                    const schedule = b.booking_schedule;
                    const isEmi = !!schedule && schedule.payment_plan && schedule.payment_plan !== "full";
                    const totalEmis = isEmi ? (schedule.n_installments || 0) : null;
                    const paidEmis = isEmi ? (b.installments_charged || 0) : null;
                    // Collected = 1% upfront fee + (EMIs paid × per_emi). For
                    // Full-plan completed bookings, the whole total has been
                    // collected. Cancelled/pending bookings reflect the actual
                    // partial collection at time of cancellation.
                    let collected = 0;
                    if (b.payment_status === "completed" && !isEmi) {
                      collected = b.total_cost || 0;
                    } else if (b.payment_status === "completed" && isEmi) {
                      // EMI fully completed → total
                      collected = b.total_cost || 0;
                    } else if ((b.payment_status === "upfront_paid" || b.payment_status === "cancelled") && isEmi) {
                      const perEmi = schedule.per_emi_inr || 0;
                      collected = (b.booking_cost || 0) + (paidEmis * perEmi);
                    } else if (b.payment_status === "upfront_paid" && !isEmi) {
                      collected = b.booking_cost || 0;
                    }

                    return (
                      <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="p-3 text-white/80">{fmtDateTime(b.created_at)}</td>
                        <td className="p-3 text-white/80">
                          {b.customer ? `${b.customer.name || "—"} (${b.customer.phone || b.customer.email || ""})` : <span className="text-white/30">—</span>}
                        </td>
                        <td className="p-3 text-white/60">{b.destination_name}</td>
                        <td className="p-3 text-white/60 capitalize">{b.package_type}</td>
                        <td className="p-3 text-white">{fmtINR(b.total_cost)}</td>
                        <td className="p-3 text-white/80" data-testid={`booking-total-emis-${b.id?.slice(0, 8)}`}>
                          {totalEmis === null ? <span className="text-white/30">—</span> : totalEmis}
                        </td>
                        <td className="p-3 text-white/80" data-testid={`booking-paid-emis-${b.id?.slice(0, 8)}`}>
                          {paidEmis === null ? (
                            <span className="text-white/30">—</span>
                          ) : (
                            <span className={paidEmis === totalEmis ? "text-emerald-400" : "text-[#D4AF37]"}>
                              {paidEmis} / {totalEmis}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-white" data-testid={`booking-collected-${b.id?.slice(0, 8)}`}>
                          {collected > 0 ? fmtINR(collected) : <span className="text-white/30">—</span>}
                        </td>
                        <td className="p-3">
                          <span className={`capitalize ${b.payment_status === "completed" ? "text-green-400" : "text-amber-400"}`}>
                            {b.payment_status || "pending"}
                          </span>
                        </td>
                        <td className="p-3">
                          <Button size="sm" variant="ghost" onClick={() => openBookingDetail(b)} data-testid={`admin-view-booking-${b.id?.slice(0, 8)}`}>
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {bookings.length === 0 && (
                    <tr><td colSpan={10} className="p-6 text-center text-white/30">No bookings</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EMI PAUSES TAB */}
        {tab === "emi-pauses" && (
          <div data-testid="admin-emi-pauses" className="space-y-6">
            {!pauseStats ? (
              <p className="text-white/40 text-sm">Loading…</p>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div
                    className="bg-white/[0.03] border border-white/5 rounded-lg p-4"
                    data-testid="emi-paused-count-card"
                  >
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2">Currently Paused</p>
                    <p className="text-3xl text-white font-light">{pauseStats.currently_paused_count}</p>
                    <p className="text-amber-400/80 text-xs mt-1">
                      out of {pauseStats.currently_paused_count + pauseStats.active_count} EMI mandates
                    </p>
                  </div>
                  <div
                    className="bg-white/[0.03] border border-white/5 rounded-lg p-4"
                    data-testid="emi-pause-rate-card"
                  >
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2">Pause Rate</p>
                    <p className="text-3xl text-white font-light">{pauseStats.pause_rate_pct}%</p>
                    <p className="text-[#D4AF37] text-xs mt-1">
                      {pauseStats.pause_rate_pct > 15 ? "Above healthy threshold" : "Healthy"}
                    </p>
                  </div>
                  <div
                    className="bg-white/[0.03] border border-white/5 rounded-lg p-4"
                    data-testid="emi-avg-days-card"
                  >
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2">Avg Days Paused</p>
                    <p className="text-3xl text-white font-light">{pauseStats.avg_days_paused}</p>
                    <p className="text-white/40 text-xs mt-1">across currently-paused mandates</p>
                  </div>
                </div>

                {/* Breakdowns */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <h3 className="text-white/80 text-sm font-medium mb-3">Pauses by Destination</h3>
                    <div className="space-y-1.5 text-sm">
                      {pauseStats.by_destination.map((d) => (
                        <div
                          key={d.destination_id}
                          className="flex justify-between text-white/60"
                          data-testid={`emi-pause-by-dest-${d.destination_id}`}
                        >
                          <span className="truncate mr-3">{d.destination_name}</span>
                          <span className="text-white">{d.paused_count}</span>
                        </div>
                      ))}
                      {pauseStats.by_destination.length === 0 && (
                        <p className="text-white/30 text-xs">No paused mandates yet</p>
                      )}
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <h3 className="text-white/80 text-sm font-medium mb-3">Pauses by Package Tier</h3>
                    <div className="space-y-1.5 text-sm">
                      {["luxury", "premium", "basic"].map((tier) => (
                        <div
                          key={tier}
                          className="flex justify-between text-white/60"
                          data-testid={`emi-pause-by-pkg-${tier}`}
                        >
                          <span className="capitalize">{tier}</span>
                          <span className="text-white">{pauseStats.by_package[tier] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Paused bookings table */}
                <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-hidden">
                  <div className="flex justify-between items-center px-4 py-3 border-b border-white/5">
                    <h3 className="text-white/80 text-sm font-medium">
                      Currently Paused Bookings ({pauseStats.paused_bookings.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white/[0.02] text-white/40 uppercase tracking-wider">
                        <tr>
                          <th className="text-left p-3 font-normal">Days Paused</th>
                          <th className="text-left p-3 font-normal">Customer</th>
                          <th className="text-left p-3 font-normal">Destination</th>
                          <th className="text-left p-3 font-normal">Package</th>
                          <th className="text-left p-3 font-normal">Charged</th>
                          <th className="text-left p-3 font-normal">Total</th>
                          <th className="text-left p-3 font-normal"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pauseStats.paused_bookings.map((b) => (
                          <tr
                            key={b.id}
                            className="border-b border-white/5 hover:bg-white/[0.02]"
                            data-testid={`emi-paused-row-${b.id?.slice(0, 8)}`}
                          >
                            <td className="p-3 text-amber-400">{b.days_paused}d</td>
                            <td className="p-3 text-white/80">
                              {b.customer_name || "—"}
                              <span className="block text-white/40 text-[10px] mt-0.5">
                                {b.customer_phone || b.customer_email || ""}
                              </span>
                            </td>
                            <td className="p-3 text-white/60">{b.destination_name || "—"}</td>
                            <td className="p-3 text-white/60 capitalize">{b.package_type}</td>
                            <td className="p-3 text-white/60">{b.installments_charged}</td>
                            <td className="p-3 text-white">{fmtINR(b.total_cost)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-1.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openBookingDetail({ id: b.id })}
                                  data-testid={`emi-paused-view-${b.id?.slice(0, 8)}`}
                                >
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/bookings/${b.id}/resume-nudge`);
                                      toast.success(`Nudge sent to ${b.customer_name || "customer"}`);
                                    } catch (err) {
                                      toast.error(
                                        "Nudge failed: " +
                                          (err.response?.data?.detail || err.message)
                                      );
                                    }
                                  }}
                                  data-testid={`emi-paused-nudge-${b.id?.slice(0, 8)}`}
                                >
                                  Nudge
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {pauseStats.paused_bookings.length === 0 && (
                          <tr>
                            <td colSpan={7} className="p-6 text-center text-white/30">
                              No paused EMI mandates.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "quote" && <QuoteBuilderTab />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "invoices" && <InvoiceGeneratorTab />}
      </div>

      <BookingDetailModal
        booking={detailBooking}
        open={!!detailBooking}
        onClose={() => setDetailBooking(null)}
      />
    </div>
  );
}
